import { CheckCircle, XCircle, Circle } from 'lucide-react';
import { cn } from '@centraoneplus/ui';

interface ExecutionProgressBarProps {
  completedCount: number;
  totalCount: number;
  failedIndex?: number;
  className?: string;
}

/**
 * Shows execution progress as X of Y actions completed.
 * Visualizes which actions completed, which failed, and which were skipped.
 */
export function ExecutionProgressBar({
  completedCount,
  totalCount,
  failedIndex,
  className,
}: ExecutionProgressBarProps) {
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const hasFailure = failedIndex !== undefined && failedIndex >= 0;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Progress text */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600 dark:text-gray-400">
          Actions completed
        </span>
        <span className="font-medium text-gray-900 dark:text-white">
          {completedCount} / {totalCount}
        </span>
      </div>

      {/* Progress bar */}
      <div className="relative h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        {/* Completed portion (green) */}
        <div
          className={cn(
            'absolute left-0 top-0 h-full rounded-full transition-all duration-300',
            hasFailure ? 'bg-yellow-500' : 'bg-green-500'
          )}
          style={{ width: `${progressPercentage}%` }}
        />
        {/* Failed portion (red) - shows one unit after the completed */}
        {hasFailure && totalCount > 0 && (
          <div
            className="absolute top-0 h-full bg-red-500 rounded-full"
            style={{
              left: `${progressPercentage}%`,
              width: `${100 / totalCount}%`,
            }}
          />
        )}
      </div>

      {/* Step indicators for small action counts */}
      {totalCount <= 10 && totalCount > 0 && (
        <div className="flex items-center gap-1 justify-center pt-1">
          {Array.from({ length: totalCount }).map((_, index) => {
            const isCompleted = index < completedCount;
            const isFailed = hasFailure && index === failedIndex;
            const isSkipped = hasFailure && index > failedIndex;

            return (
              <div
                key={index}
                className={cn(
                  'flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium',
                  isCompleted && !isFailed && 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                  isFailed && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                  isSkipped && 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500',
                  !isCompleted && !isFailed && !isSkipped && 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                )}
                title={
                  isFailed
                    ? `Action ${index + 1} failed`
                    : isCompleted
                    ? `Action ${index + 1} completed`
                    : isSkipped
                    ? `Action ${index + 1} skipped`
                    : `Action ${index + 1} pending`
                }
              >
                {isFailed ? (
                  <XCircle className="w-4 h-4" />
                ) : isCompleted ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <Circle className="w-3 h-3" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ExecutionProgressBar;
